import { Button, Input, Label } from "@safeplate/ui";
import { Apple, ArrowRight, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";

import {
  requestPasswordReset,
  signInWithEmail,
  signInWithSocialProvider,
  signUpWithEmail,
} from "../api/auth.js";
import { displayError } from "../api/errors.js";
import { Brand } from "../components/brand.js";

export type AuthMode = "sign-in" | "sign-up";

function AuthFrame({
  children,
  kicker,
}: {
  children: ReactNode;
  kicker: string;
}) {
  return (
    <main className="grid min-h-dvh bg-canvas lg:grid-cols-[minmax(360px,0.9fr)_minmax(520px,1.1fr)]">
      <section className="relative hidden overflow-hidden bg-emerald-800 p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="auth-grid" aria-hidden="true" />
        <div className="relative z-10 [&_a]:text-white">
          <Brand />
        </div>
        <div className="relative z-10 max-w-lg pb-10">
          <p className="mb-5 text-xs font-bold tracking-[0.16em] text-emerald-300 uppercase">
            {kicker}
          </p>
          <h1 className="text-5xl leading-[1.03] font-black tracking-[-0.055em] text-balance">
            Every food-safety record, ready when your verifier asks.
          </h1>
          <p className="mt-6 max-w-md text-base leading-7 text-emerald-100/80">
            Keep daily checks moving in the kitchen, review what needs
            attention, and produce clear evidence without rebuilding a year of
            paperwork.
          </p>
        </div>
        <p className="relative z-10 text-xs text-emerald-200/60">
          Built for New Zealand hospitality teams using Simply Safe &amp;
          Suitable.
        </p>
      </section>
      <section className="flex items-center justify-center px-5 py-10 sm:px-10">
        <div className="w-full max-w-md">
          <div className="mb-10 lg:hidden">
            <Brand />
          </div>
          {children}
        </div>
      </section>
    </main>
  );
}

function SocialButton({
  disabled,
  onClick,
  provider,
}: {
  disabled: boolean;
  onClick: () => void;
  provider: "apple" | "google";
}) {
  return (
    <Button
      aria-label={`Continue with ${provider === "apple" ? "Apple" : "Google"}`}
      className="w-full bg-surface"
      disabled={disabled}
      onClick={onClick}
      variant="outline"
    >
      {provider === "apple" ? (
        <Apple className="size-4" aria-hidden="true" />
      ) : (
        <span
          className="grid size-4 place-items-center text-sm font-black text-blue-700"
          aria-hidden="true"
        >
          G
        </span>
      )}
      {provider === "apple" ? "Apple" : "Google"}
    </Button>
  );
}

export function AuthScreen({
  beforeSocialRedirect,
  context = "default",
  mode,
  onAuthenticated,
  onSwitchMode,
}: {
  beforeSocialRedirect?: () => void;
  context?: "default" | "invitation";
  mode: AuthMode;
  onAuthenticated: () => Promise<void>;
  onSwitchMode?: () => void;
}) {
  const creatingAccount = mode === "sign-up";
  const joiningTeam = context === "invitation";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      if (creatingAccount) {
        await signUpWithEmail({
          email: email.trim(),
          name: name.trim(),
          password,
        });
        setVerificationSent(true);
      } else {
        await signInWithEmail({ email: email.trim(), password });
        await onAuthenticated();
      }
    } catch (caught) {
      setError(displayError(caught));
    } finally {
      setPending(false);
    }
  }

  async function social(provider: "apple" | "google") {
    setError(null);
    setPending(true);
    try {
      beforeSocialRedirect?.();
      const redirectUrl = await signInWithSocialProvider(provider);
      if (redirectUrl) {
        globalThis.location.assign(redirectUrl);
      } else {
        await onAuthenticated();
      }
    } catch (caught) {
      setError(displayError(caught));
      setPending(false);
    }
  }

  if (verificationSent) {
    return (
      <AuthFrame kicker={joiningTeam ? "Invitation saved" : "Account created"}>
        <div className="rounded-3xl border border-emerald-200 bg-surface p-8 shadow-lg">
          <span className="grid size-12 place-items-center rounded-2xl bg-emerald-100 text-emerald-800">
            <CheckCircle2 className="size-6" aria-hidden="true" />
          </span>
          <h2 className="mt-6 text-3xl font-black tracking-[-0.04em] text-stone-950">
            Check your email
          </h2>
          <p className="mt-3 text-sm leading-6 text-stone-600">
            We sent a verification link to <strong>{email}</strong>. Open it to
            finish setting up your SafePlate account.
            {joiningTeam
              ? " Then return to your invitation and sign in to join the team."
              : null}
          </p>
          {onSwitchMode ? (
            <button
              className="mt-7 inline-flex text-sm font-bold text-emerald-800 hover:text-emerald-950"
              onClick={onSwitchMode}
              type="button"
            >
              Back to sign in
            </button>
          ) : (
            <Link
              className="mt-7 inline-flex text-sm font-bold text-emerald-800 hover:text-emerald-950"
              to="/sign-in"
            >
              Back to sign in
            </Link>
          )}
        </div>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame
      kicker={
        joiningTeam
          ? "Join your workplace"
          : creatingAccount
            ? "Start with SafePlate"
            : "Welcome back"
      }
    >
      <div className="mb-8">
        <p className="text-xs font-bold tracking-[0.14em] text-emerald-800 uppercase">
          {creatingAccount ? "Create your account" : "Sign in"}
        </p>
        <h2 className="mt-3 text-4xl font-black tracking-[-0.045em] text-stone-950">
          {creatingAccount
            ? joiningTeam
              ? "Create your account"
              : "Set up your food business"
            : "Good to see you again"}
        </h2>
        <p className="mt-3 text-sm leading-6 text-stone-600">
          {creatingAccount
            ? joiningTeam
              ? "Your invitation will connect this account to the right workplace, role and location."
              : "You’ll add your organisation and first location next."
            : "Use the account your manager or administrator invited."}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <SocialButton
          disabled={pending}
          onClick={() => void social("google")}
          provider="google"
        />
        <SocialButton
          disabled={pending}
          onClick={() => void social("apple")}
          provider="apple"
        />
      </div>
      {joiningTeam ? (
        <p className="mt-3 text-xs leading-5 text-stone-500">
          Using Apple? Choose Share My Email so Apple returns the address your
          manager invited. Otherwise, create the account below with that email.
        </p>
      ) : null}
      <div className="my-6 flex items-center gap-4 text-[0.7rem] font-bold tracking-widest text-stone-400 uppercase">
        <span className="h-px flex-1 bg-stone-300" />
        or use email
        <span className="h-px flex-1 bg-stone-300" />
      </div>
      <form className="space-y-4" onSubmit={(event) => void submit(event)}>
        {creatingAccount ? (
          <div className="space-y-2">
            <Label htmlFor="name">Your name</Label>
            <Input
              autoComplete="name"
              id="name"
              onChange={(event) => setName(event.target.value)}
              placeholder="Aroha Ngata"
              required
              value={name}
            />
          </div>
        ) : null}
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            autoComplete="email"
            id="email"
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@restaurant.co.nz"
            required
            type="email"
            value={email}
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="password">Password</Label>
            {!creatingAccount ? (
              <Link
                className="text-xs font-bold text-emerald-800 hover:text-emerald-950"
                to="/forgot-password"
              >
                Forgot password?
              </Link>
            ) : null}
          </div>
          <div className="relative">
            <Input
              autoComplete={
                creatingAccount ? "new-password" : "current-password"
              }
              className="pr-12"
              id="password"
              minLength={10}
              onChange={(event) => setPassword(event.target.value)}
              required
              type={passwordVisible ? "text" : "password"}
              value={password}
            />
            <button
              aria-label={passwordVisible ? "Hide password" : "Show password"}
              className="absolute inset-y-0 right-0 grid w-11 place-items-center text-stone-400 hover:text-stone-700"
              onClick={() => setPasswordVisible((visible) => !visible)}
              type="button"
            >
              {passwordVisible ? (
                <EyeOff className="size-4" />
              ) : (
                <Eye className="size-4" />
              )}
            </button>
          </div>
          {creatingAccount ? (
            <p className="text-xs leading-5 text-stone-500">
              Use at least 10 characters.
            </p>
          ) : null}
        </div>
        {error ? (
          <p
            className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-800"
            role="alert"
          >
            {error}
          </p>
        ) : null}
        <Button className="mt-2 w-full" disabled={pending} type="submit">
          {pending
            ? "Please wait…"
            : creatingAccount
              ? "Create account"
              : "Sign in"}
          {!pending ? (
            <ArrowRight className="size-4" aria-hidden="true" />
          ) : null}
        </Button>
      </form>
      <p className="mt-7 text-center text-sm text-stone-600">
        {creatingAccount ? "Already have an account?" : "New to SafePlate?"}{" "}
        {onSwitchMode ? (
          <button
            className="font-bold text-emerald-800 hover:text-emerald-950"
            onClick={onSwitchMode}
            type="button"
          >
            {creatingAccount ? "Sign in" : "Create one"}
          </button>
        ) : (
          <Link
            className="font-bold text-emerald-800 hover:text-emerald-950"
            to={creatingAccount ? "/sign-in" : "/sign-up"}
          >
            {creatingAccount ? "Sign in" : "Create one"}
          </Link>
        )}
      </p>
    </AuthFrame>
  );
}

export function ForgotPasswordScreen() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (caught) {
      setError(displayError(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthFrame kicker="Account access">
      <p className="text-xs font-bold tracking-[0.14em] text-emerald-800 uppercase">
        Reset your password
      </p>
      <h2 className="mt-3 text-4xl font-black tracking-[-0.045em] text-stone-950">
        {sent ? "Check your email" : "Get a secure reset link"}
      </h2>
      <p className="mt-3 text-sm leading-6 text-stone-600">
        {sent
          ? "If that address belongs to a SafePlate account, a reset link is on its way."
          : "Enter your account email. For privacy, the response is the same whether or not an account exists."}
      </p>
      {sent ? (
        <Link
          className="mt-7 inline-flex text-sm font-bold text-emerald-800"
          to="/sign-in"
        >
          Back to sign in
        </Link>
      ) : (
        <form
          className="mt-7 space-y-4"
          onSubmit={(event) => void submit(event)}
        >
          <div className="space-y-2">
            <Label htmlFor="reset-email">Email</Label>
            <Input
              autoComplete="email"
              id="reset-email"
              onChange={(event) => setEmail(event.target.value)}
              required
              type="email"
              value={email}
            />
          </div>
          {error ? (
            <p className="text-sm text-red-800" role="alert">
              {error}
            </p>
          ) : null}
          <Button className="w-full" disabled={pending} type="submit">
            {pending ? "Sending…" : "Send reset link"}
          </Button>
        </form>
      )}
    </AuthFrame>
  );
}
